import { type FormEvent, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { AuthLayout } from '../components/AuthLayout'
import { Button, Field, FormError, Toggle } from '../components/ui'
import { ApiError, api, call, messageOf } from '../lib/api'
import { requireUser, safeNext } from '../lib/auth'
import { meQuery } from '../lib/queries'

// Shown once, right after a first sign-in with Google. We guessed a username
// from their name and turned reminders on; this is where they see both and
// can change either before going any further.
export const Route = createFileRoute('/onboarding')({
  validateSearch: (s: Record<string, unknown>): { next?: string } => ({ next: safeNext(s.next) }),
  beforeLoad: ({ context, location }) => requireUser(context.queryClient, location.href),
  component: Onboarding,
})

function Onboarding() {
  const { next } = Route.useSearch()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: me } = useSuspenseQuery(meQuery)
  const [name, setName] = useState(me.user.display_name)
  const [username, setUsername] = useState(me.user.username)
  const [reminders, setReminders] = useState(
    () => me.notifications?.find((r) => r.category === 'round')?.channels?.find((c) => c.channel === 'email')?.on ?? true,
  )

  const save = useMutation({
    mutationFn: () => call(api.PATCH('/me', { body: { display_name: name, username, notifications: [{ category: 'round', channel: 'email', on: reminders }] } })),
    onSuccess: (updated) => {
      qc.setQueryData(meQuery.queryKey, updated)
      navigate({ to: next ?? '/' })
    },
  })
  const fields = save.error instanceof ApiError ? save.error.fields : {}
  const submit = (e: FormEvent) => {
    e.preventDefault()
    save.mutate()
  }

  return (
    <AuthLayout title="You're in" lede={`Signed in as ${me.user.email}. Two things to check, then on to the cats.`}>
      <form onSubmit={submit} className="grid gap-5">
        <FormError>{save.isError && messageOf(save.error)}</FormError>
        <Field label="Your name" name="name" autoComplete="name" required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} error={fields.display_name} help="What the other managers see." />
        <Field label="Username" name="username" autoComplete="username" autoCapitalize="none" spellCheck={false} required minLength={3} maxLength={32} pattern="[A-Za-z0-9_]+" value={username} onChange={(e) => setUsername(e.target.value)} error={fields.username} help="We guessed this from your name. Change it if you like." />
        <div className="rounded-card border border-line bg-surface p-4">
          <Toggle id="onboarding-reminders" label="Email me round reminders" checked={reminders} onChange={setReminders}
            help="When a round opens, the day before it closes, when voting starts, and when results post. You can change this any time from your account." />
        </div>
        <Button type="submit" variant="primary" block busy={save.isPending}>
          Looks good
        </Button>
      </form>
    </AuthLayout>
  )
}
